import type {
  SignInWithAppleOptions,
  SignInWithAppleResponse,
} from '@capacitor-community/apple-sign-in';
import { SignInWithApple } from '@capacitor-community/apple-sign-in';
import { GoogleAuth } from '@codetrix-studio/capacitor-google-auth';
import { PostLoginRequestError } from '@pickthebank/api-sdk/request/auth/post-login.request';
import { useGoogleLogin } from '@react-oauth/google';
import error from 'next/error';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next-translate-routes/router';
import type { ChangeEvent, FC, MouseEvent } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import AppleSignin from 'react-apple-signin-auth';

import { Button, CheckboxInput } from '@/components';
import { AppleButton } from '@/components/Auth/AppleButton';
import { GoogleButton } from '@/components/Auth/GoogleButton';
import { getFormContextByType } from '@/components/Dialog/parts/SignUpForm/singUpForm.utils';
import { useAnalytics } from '@/contexts/AnalyticsContext';
import { useApi } from '@/contexts/ApiContext';
import { signUp } from '@/services/supabase';
import { useAuthModalsControl, useAuthStore } from '@/store';
import { AnalyticsCategory, AnalyticsEvent } from '@/types/analytics';
import { analyticServices, capitalizeFirstLetter } from '@/utils';
import { isAndroidApp, isMobileApp } from '@/utils/isMobileApp';
import { createArrayWithActiveSubscriptions } from '@/utils/subscriptions';

import { DialogTerms } from './parts';

const APPLE_SIGN_IN_OPTIONS: SignInWithAppleOptions = {
  clientId: process.env.NEXT_PUBLIC_OAUTH_APPLE_CLIENT_ID as string,
  redirectURI: `https://${process.env.NEXT_PUBLIC_MAIN_DOMAIN}`,
  scopes: 'email name',
  nonce: 'nonce',
};

interface ISignUpFormProps {
  onSubmit: () => void;
  executeCaptcha: () => Promise<string>;
}

export const SignUpForm: FC<ISignUpFormProps> = ({
  onSubmit,
  executeCaptcha,
}) => {
  const router = useRouter();
  const { t } = useTranslation();
  const {
    setEmail,
    errors,
    setError,
    token: userToken,
    email,
    setToken,
  } = useAuthStore((state) => state);
  const inputRef = useRef<HTMLInputElement>(null);
  const [emailField, setEmailField] = useState(userToken ? email : '');
  const ApiSingleton = useApi();

  const { analytics } = useAnalytics();

  const { type, setCloseModal, redirectUri } = useAuthModalsControl(
    (state) => state,
  );
  const { description, button } = getFormContextByType(type);
  const [isSubscribed, setIsSubscribed] = useState(false);

  const {
    putProfile,
    postGoogleAuthRequest,
    postAppleAuthRequest,
    postAppleAppAuthRequest,
  } = useApi();

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const handleSubmit = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    try {
      const subs =
        isSubscribed || type === 'subscribe'
          ? createArrayWithActiveSubscriptions()
          : [];
      if (userToken) {
        await putProfile({ subscriptions: subs });
        setCloseModal();
      } else {
        const token = await executeCaptcha();
        await signUp(
          emailField,
          router.locale || 'en',
          token,
          analytics,
          subs,
          redirectUri || undefined,
        );
      }
      analyticServices.sendEvent({
        action: AnalyticsEvent.authModalEmailSubmitted,
        label: AnalyticsCategory.auth,
      });
      onSubmit();
      setEmail(emailField);
    } catch (err: any) {
      if (
        err.response.data.message ===
        PostLoginRequestError.VERIFICATION_MESSAGE_HAS_SENT
      ) {
        setEmail(emailField);
      }
      setError([...err.response.data.message]);
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (errors.length > 0) {
      setError([]);
    }
    setEmailField(e.target.value);
  };

  useEffect(() => {
    setError([]);
  }, []);

  const getPayloadForAuthRequest = () => {
    return {
      language: router.locale || 'en',
      analyticsData: analytics,
    };
  };

  const onOAuthSuccess = (accessToken: string) => {
    ApiSingleton.setToken(accessToken);
    setToken(accessToken);
    setCloseModal();
    if (redirectUri) {
      router.push(redirectUri);
    }
  };

  const handleGoogleLogin = async (token: string) => {
    const payload = {
      access_token: token,
      ...getPayloadForAuthRequest(),
    };
    const result: any = await postGoogleAuthRequest(payload);
    if (result) {
      onOAuthSuccess(result.access_token);
    }
  };

  const handleAppleLogin = async (
    token: string,
    code: string,
    clientId: string,
    loginType: 'web' | 'app',
  ) => {
    const payload = {
      id_token: token,
      code,
      ...getPayloadForAuthRequest(),
      // subs,
    };
    if (loginType === 'app') {
      const result: any = await postAppleAppAuthRequest(payload, clientId);
      if (result) {
        onOAuthSuccess(result.access_token);
      }
    } else {
      const result: any = await postAppleAuthRequest(payload, clientId);
      if (result) {
        onOAuthSuccess(result.access_token);
      }
    }
  };

  const login = useGoogleLogin({
    flow: 'implicit',
    onSuccess: async (tokenResponse) => {
      handleGoogleLogin(tokenResponse.access_token);
    },
    onError: (e) => {
      console.error(e);
    },
  });

  const loginApp = async () => {
    try {
      const data = await GoogleAuth.signIn();
      handleGoogleLogin(data.authentication.accessToken);
    } catch (e) {
      console.log(e);
    }
  };

  const loginAppleWeb = async (response: any) => {
    handleAppleLogin(
      response.authorization.id_token,
      response.authorization.code,
      APPLE_SIGN_IN_OPTIONS.clientId,
      'web',
    );
  };

  const loginAppApple = () => {
    SignInWithApple.authorize(APPLE_SIGN_IN_OPTIONS)
      .then((result: SignInWithAppleResponse) => {
        handleAppleLogin(
          result.response.identityToken,
          result.response.authorizationCode,
          'eu.pickthebank.app',
          'app',
        );
      })
      .catch((e) => {
        console.log(e);
      });
  };

  return (
    <>
      <div
        className="mt-4 flex w-full content-center justify-center gap-4 md:mt-6"
        onClick={(e) => e.stopPropagation()}
      >
        <form
          className="w-full"
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <p className="body2 mb-4 text-grey-800">{t(description)}</p>
          <div className="mb-4  flex flex-col gap-2">
            <GoogleButton
              onClick={() => {
                if (isMobileApp()) {
                  loginApp();
                } else {
                  login();
                }
              }}
            />
            {isMobileApp() && !isAndroidApp() && (
              <AppleButton
                onClick={() => {
                  try {
                    loginAppApple();
                  } catch (e) {
                    console.log(e);
                  }
                }}
              />
            )}
            {!isMobileApp() && !isAndroidApp() && (
              <AppleSignin
                authOptions={{
                  /** Client ID - eg: 'com.example.com' */
                  clientId: process.env.NEXT_PUBLIC_OAUTH_APPLE_CLIENT_ID || '',
                  /** Requested scopes, seperated by spaces - eg: 'email name' */
                  scope: APPLE_SIGN_IN_OPTIONS.scopes || '',
                  /** Apple's redirectURI - must be one of the URIs you added to the serviceID - the undocumented trick in apple docs is that you should call auth from a page that is listed as a redirectURI, localhost fails */
                  redirectURI: APPLE_SIGN_IN_OPTIONS.redirectURI,
                  nonce: APPLE_SIGN_IN_OPTIONS.nonce,
                  usePopup: true,
                }}
                className="h-[56px] !rounded-full"
                onSuccess={loginAppleWeb}
                onError={(err: any) => console.error(err)}
                uiType="dark"
              />
            )}
          </div>
          <p
            className={`mb-4 flex items-center gap-4 before:block before:h-px before:w-full before:bg-blue-100 before:content-[""]   after:block after:h-px after:w-full after:bg-blue-100 after:content-[""]`}
          >
            <span className="body3 font-inter shrink-0 text-grey-500">
              {t('login.or_login_with_email')}
            </span>
          </p>
          <div className="relative w-full">
            <input
              ref={inputRef}
              value={emailField}
              onChange={handleChange}
              disabled={!!userToken}
              type="email"
              name="email"
              id="email"
              className="input-field body3 md:body2 h-[44px] w-full rounded-[31px] border-[1.25px] border-grey-200 px-5 disabled:cursor-not-allowed disabled:bg-grey-300 disabled:text-grey-600 md:h-[56px] md:px-7"
              placeholder={t('login.subscribe.placeholder_email_text')}
            />
          </div>
          <Button
            color="blue"
            className="mt-2 h-[44px] w-full text-[13px] sm:h-[56px] md:text-[15px]"
            props={{
              onClick: handleSubmit,
              disabled: !emailField || error.length > 0,
            }}
          >
            {t(button)}
          </Button>
          {errors.length > 0 &&
            errors.map((err) => (
              <span
                key={err}
                className="text-[11px] font-medium leading-[16px] tracking-[0.2px] text-red-500"
              >
                {capitalizeFirstLetter(err)}
              </span>
            ))}
          {type !== 'subscribe' && (
            <CheckboxInput
              label={t('login.subscribe_checkbox')}
              checked={isSubscribed}
              onChange={setIsSubscribed}
              className="mt-4 items-start"
              checkboxClassName="mt-1.5 h-5 w-5 shrink-0"
              labelClassName="ml-2 text-[13px] font-medium tracking-[0.1px]"
            />
          )}
        </form>
      </div>
      <DialogTerms />
    </>
  );
};
